import React, { useState } from "react"
import { AliceContactSettings, useAliceContactSettings } from "../../components/forms/aliceContactSettings"

/**
 * Onboarding step for how Alice may reach the member — the same
 * `AliceContactSettings` control used on the Edit Profile screen, given its own
 * step so it's actually seen during onboarding rather than only on a screen the
 * member might never revisit. Skippable like the other optional steps
 * (enableTwoFactor, the invite-spouse section of fillProfile) — completion is
 * recorded either way, and the defaults on `useAliceContactSettings` (both
 * channels on, no health-data consent yet) still apply until the member visits
 * Settings or Edit Profile.
 */
export function OnboardingConsent({ onComplete }: { onComplete: () => void }) {
  const contactSettings = useAliceContactSettings()
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!(e.target as HTMLFormElement).checkValidity()) { return }

    setSubmitting(true)
    try {
      await contactSettings.save()
      onComplete()
    } catch {
      // Already captured by useAsyncAction and surfaced via contactSettings.saveError
      // below; swallow here so we stay on the step instead of an unhandled rejection.
    } finally {
      setSubmitting(false)
    }
  }

  return <div className="row mt-4">
    <div className="col-12 col-xl-8 offset-xl-2">
      <h1>Stay in the loop</h1>
      <p>
        Alice, our AI assistant, can let you know when a bill needs your attention,
        when she's heard back from a provider, and when something is due. Tell us
        how she should reach you — you can change this anytime from your settings.
      </p>

      <form onSubmit={onSubmit}>
        <AliceContactSettings {...contactSettings} />

        <div className="col-12 mt-3 d-flex gap-2">
          <button type="submit" className="btn btn-primary"
            disabled={!contactSettings.isValid || submitting}>
            {submitting ? 'Saving…' : 'Continue'}
          </button>
          <button type="button" className="btn btn-link"
            disabled={submitting}
            onClick={() => onComplete()}>
            Skip for now
          </button>
        </div>
      </form>
    </div>
  </div>
}
